/**
 * Accessoires selon la fixation — Moment Cocooning, 26 août 2026.
 *
 * Reprend le comportement de assets/toggle-cross-sell.js, sous des noms
 * d'éléments à nous, et lui ajoute la seule chose qui manquait au thème :
 * l'affichage conditionné par la valeur d'option choisie sur la fiche.
 *
 * Pourquoi du script et pas du Liquid : le thème met la fiche à jour dans le
 * navigateur, sans redemander la page au serveur. Le rechargement de section au
 * changement de variante est écrit puis mis en commentaire aux lignes 89 à 94
 * de assets/variant-picker.js. Il n'existe donc aucune solution en Liquid seul.
 *
 * La règle qui vaut la mission : une ligne qui disparaît est DÉCOCHÉE. Sans
 * cela, la cliente passe des œillets au galon fronceur, la tringle sort de
 * l'écran mais reste dans le panier, et on lui vend l'accessoire qui ne va pas
 * avec son rideau.
 */
import { formatMoney } from '@theme/utilities';
import { ToggleCrossSellsUpdatedEvent, ThemeEvents, CartUpdatedEvent } from '@theme/events';

/** Compare deux valeurs d'option sans se laisser arrêter par la casse,
 *  les accents, la ligature œ ou une espace de trop. */
function normaliser(valeur) {
  return String(valeur ?? '')
    .trim()
    .toLowerCase()
    .replace(/\u0153/g, 'oe')
    .replace(/\u00e6/g, 'ae')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Remet une ligne à l'état non retenu. Écrite en fonction libre et non en
 *  méthode : la liste se met à niveau AVANT les lignes qu'elle contient, et
 *  au premier passage `ligne.decocher` n'existe pas encore.
 *  Renvoie vrai si quelque chose a effectivement été décoché. */
function decocherLigne(ligne) {
  let change = false;

  const checkbox = ligne.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
  if (checkbox != null && checkbox.checked === true) {
    checkbox.checked = false;
    change = true;
  }

  const button = ligne.querySelector('[data-ref="toggle-cross-sell-button"]');
  if (button != null && button.classList.contains('button--active') === true) {
    button.classList.remove('button--active');
    button.classList.add('button--secondary');
    button.removeAttribute('data-deja-ajoute');
    change = true;
  }

  return change;
}

class ToggleCrossSellFixationList extends HTMLElement {
  constructor() {
    super();
    this.crossSells = [];
    this.onVariantChange = this.appliquer.bind(this);
    this.onCartUpdated = this.onCartUpdated.bind(this);
  }

  connectedCallback() {
    this.crossSells = this.querySelectorAll('[data-ref="toggle-cross-sell"]');

    document.addEventListener(ThemeEvents.variantSelected, this.onVariantChange);
    document.addEventListener(ThemeEvents.variantUpdated, this.onVariantChange);

    document.addEventListener(ThemeEvents.cartUpdated, this.onCartUpdated);

    // Premier passage : la fiche arrive avec une fixation déjà retenue.
    this.appliquer(null, true);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantSelected, this.onVariantChange);
    document.removeEventListener(ThemeEvents.variantUpdated, this.onVariantChange);
    document.removeEventListener(ThemeEvents.cartUpdated, this.onCartUpdated);
  }

  /** 02/10/2026, comme Mon Mocassin : une case cochée est décochée une fois
   *  l'ajout fait depuis le formulaire produit, sinon l'accessoire repart une
   *  seconde fois avec la paire suivante. On ne réagit qu'à `product-form` :
   *  l'ajout d'un accessoire par son propre bouton émet `toggle-cross-sell`. */
  onCartUpdated(event) {
    if (event?.detail?.data?.source !== 'product-form') return;
    let change = false;
    this.querySelectorAll('[data-ref="toggle-cross-sell"]').forEach((ligne) => {
      const checkbox = ligne.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
      if (checkbox != null && checkbox.checked === true) {
        checkbox.checked = false;
        change = true;
      }
    });
    if (change) document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  /** Le formulaire qui porte le sélecteur de déclinaison. On y reste enfermé
   *  pour ne pas lire la fixation d'une autre fiche présente sur la page,
   *  une recommandation ou un ajout rapide par exemple. */
  get portee() {
    return this.closest('form') ?? this.closest('.product-form__form') ?? document;
  }

  /** La valeur d'option retenue en ce moment sur la fiche. Le sélecteur de
   *  déclinaison remplace son propre DOM à chaque mise à jour : on relit donc
   *  à chaque fois, on ne garde aucune référence. */
  get valeurRetenue() {
    const nom = `option-${this.dataset.optionName ?? 'Fixation'}`;
    const liste = this.portee.querySelector(`select[name="${CSS.escape(nom)}"]`);
    if (liste != null) return liste.value;

    const coche = this.portee.querySelector(`input[name="${CSS.escape(nom)}"]:checked`);
    if (coche != null) return coche.value;

    return '';
  }

  /**
   * Montre les lignes qui correspondent à la fixation, masque les autres,
   * et décoche ce qu'elle masque.
   * @param {Event|null} event
   * @param {boolean} silencieux - au premier passage, rien n'est encore coché :
   *   inutile de réveiller le formulaire pour recalculer un prix inchangé.
   */
  appliquer(event = null, silencieux = false) {
    const retenue = normaliser(this.valeurRetenue);
    let decoche = false;

    this.crossSells = this.querySelectorAll('[data-ref="toggle-cross-sell"]');

    this.crossSells.forEach((ligne) => {
      const brut = ligne.dataset.values ?? '';
      const valeurs = brut
        .split(',')
        .map(normaliser)
        .filter((v) => v.length > 0);

      // Un groupe sans valeur est universel : il ne se masque jamais.
      const visible = valeurs.length === 0 || valeurs.includes(retenue);

      if (visible) {
        ligne.hidden = false;
        return;
      }

      if (decocherLigne(ligne) === true) decoche = true;
      ligne.hidden = true;
    });

    // Aucune ligne visible : on masque le bloc entier, titre compris.
    const auMoinsUne = [...this.crossSells].some((ligne) => ligne.hidden === false);
    this.hidden = auMoinsUne === false;

    // Le prix affiché sous le bouton d'achat vient de product-form.js, qui ne
    // recalcule qu'en entendant cet événement.
    if (decoche && silencieux === false) {
      document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
    }
  }

  /** Lu par assets/product-form.js pour composer le panier et le prix total.
   *  Une ligne masquée ne compte jamais, même si son état était resté coché. */
  getSelectedItems() {
    const items = [];
    let total_price = 0;
    let total_compare_at_price = 0;

    this.crossSells.forEach((crossSell) => {
      if (crossSell.hidden === true) return;

      const checkbox = crossSell.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
      const button = crossSell.querySelector('[data-ref="toggle-cross-sell-button"]');
      let retenu = false;

      if (checkbox != null && checkbox.checked === true) {
        items.push({ id: checkbox.dataset.variantId, quantity: 1 });
        retenu = true;
      }

      if (button != null && button.classList.contains('button--active') === true && button.hasAttribute('data-deja-ajoute') === false) {
        items.push({ id: button.dataset.variantId, quantity: 1 });
        retenu = true;
      }

      if (retenu) {
        total_price += parseInt(crossSell.getPrices().price);
        total_compare_at_price += parseInt(crossSell.getPrices().compareAtPrice);
      }
    });

    return { items, total_price, total_compare_at_price };
  }
}

if (!customElements.get('toggle-cross-sell-fixation-list')) {
  customElements.define('toggle-cross-sell-fixation-list', ToggleCrossSellFixationList);
}

class ToggleCrossSellFixation extends HTMLElement {
  constructor() {
    super();
    this.checkbox = null;
    this.button = null;
    this.select = null;
    this.image = null;
    this.price = null;
    this.onSelectChange = this.variantChanged.bind(this);
    this.onButtonClick = this.toggleAddButton.bind(this, undefined);
    this.onCheckboxChange = this.checkboxChanged.bind(this);
    this.onCardClick = this.handleCardClick.bind(this);
  }

  connectedCallback() {
    this.checkbox = this.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
    this.button = this.querySelector('[data-ref="toggle-cross-sell-button"]');
    this.select = this.querySelector('[data-ref="toggle-cross-sell-select"]');
    this.image = this.querySelector('[data-ref="toggle-cross-sell-image"]');
    this.price = this.querySelector('[data-ref="toggle-cross-sell-price"]');

    this.select?.addEventListener('change', this.onSelectChange);
    this.button?.addEventListener('click', this.onButtonClick);
    this.checkbox?.addEventListener('change', this.onCheckboxChange);
    this.addEventListener('click', this.onCardClick);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.onSelectChange);
    this.button?.removeEventListener('click', this.onButtonClick);
    this.checkbox?.removeEventListener('change', this.onCheckboxChange);
    this.removeEventListener('click', this.onCardClick);
  }

  /** Conservée pour qui voudrait décocher une ligne depuis l'extérieur. */
  decocher() {
    return decocherLigne(this);
  }

  handleCardClick(event) {
    if (event.target.closest('a') != null) return;
    if (event.target.closest('.select-wrapper') != null) return;

    const checkboxLabel = this.checkbox?.closest('.checkbox');
    if (checkboxLabel != null && checkboxLabel.contains(event.target)) return;
    if (this.button != null && this.button.contains(event.target)) return;

    if (this.checkbox != null) {
      this.checkbox.checked = !this.checkbox.checked;
      this.checkboxChanged();
    }
    // Mode « Bouton Ajouter » : l'ajout est réel, donc seul le bouton le
    // déclenche. Un clic sur la carte ne doit jamais remplir le panier.
  }

  checkboxChanged() {
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  toggleAddButton(active) {
    if (this.button.classList.contains('button--active') === false || active === true) {
      this.button.classList.add('button--active');
      this.button.classList.remove('button--secondary');
      this.ajouterVraiment();
    } else {
      this.button.classList.remove('button--active');
      this.button.classList.add('button--secondary');
      this.button.removeAttribute('data-deja-ajoute');
    }

    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  /** 02/10/2026, même mécanisme que Mon Mocassin : « Ajouter » met l'accessoire
   *  dans le panier tout de suite, sans dépendre du bouton de la paire. Les
   *  sections demandées sont celles du formulaire produit. Source
   *  `toggle-cross-sell` et surtout pas `product-form`, qui décocherait. */
  ajouterVraiment() {
    const id = this.button?.dataset.variantId;
    if (!id) return;
    this.button.setAttribute('disabled', 'disabled');

    const sections = ['auto-gifts'];
    const tiroir = document.querySelector('cart-drawer');
    if (tiroir?.dataset.sectionId) sections.push(tiroir.dataset.sectionId);

    fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`.replace('.js.js', '.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: sections.join(',') }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((reponse) => {
        this.button.setAttribute('data-deja-ajoute', '1');
        const compte = document.querySelector('cart-icon')?.getCurrentItemCount();
        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: compte != null ? compte + 1 : undefined,
              source: 'toggle-cross-sell',
              sections: reponse.sections,
            }
          )
        );
      })
      .catch(() => {
        this.button.classList.remove('button--active');
        this.button.classList.add('button--secondary');
        this.button.removeAttribute('data-deja-ajoute');
        document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: 'Ajout impossible' } }));
      })
      .finally(() => this.button.removeAttribute('disabled'));
  }

  variantChanged() {
    const optionSelected = this.select.options[this.select.selectedIndex];

    this.updateProductPrice(optionSelected);
    this.updateProductImage(optionSelected);
    this.updateVariantId(optionSelected);

    if (this.checkbox != null) this.checkbox.checked = true;
    if (this.button != null) {
      // Changer de déclinaison ne doit pas remplir le panier : le bouton
      // revient à « Ajouter » et la cliente décide.
      this.button.classList.remove('button--active');
      this.button.classList.add('button--secondary');
      this.button.removeAttribute('data-deja-ajoute');
    }

    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  updateProductPrice(optionSelected) {
    const variantPrice = optionSelected.dataset.price;
    const variantCompareAtPrice = optionSelected.dataset.compareAtPrice;

    const prix = this.price?.querySelector('.price');
    if (prix != null) prix.innerHTML = formatMoney(variantPrice);
    this.dataset.price = variantPrice;

    if (variantCompareAtPrice != null) {
      const barre = this.price?.querySelector('.compare-at-price');
      if (barre != null) barre.innerHTML = formatMoney(variantCompareAtPrice);
      this.dataset.compareAtPrice = variantCompareAtPrice;
    }
  }

  updateProductImage(optionSelected) {
    const variantImage = optionSelected.dataset.variantImage;
    const img = this.image?.querySelector('img');

    if (variantImage != null && img != null) img.src = variantImage;
  }

  updateVariantId(optionSelected) {
    const variantId = optionSelected.value;

    if (this.checkbox != null) this.checkbox.dataset.variantId = variantId;
    if (this.button != null) this.button.dataset.variantId = variantId;
  }

  getPrices() {
    let price = 0;
    let compareAtPrice = 0;

    if (this.dataset.price != null) price = parseInt(this.dataset.price);

    if (this.dataset.compareAtPrice != null) {
      compareAtPrice = parseInt(this.dataset.compareAtPrice);
    } else {
      compareAtPrice = parseInt(this.dataset.price);
    }

    return { price, compareAtPrice };
  }
}

if (!customElements.get('toggle-cross-sell-fixation')) {
  customElements.define('toggle-cross-sell-fixation', ToggleCrossSellFixation);
}
